import * as THREE from "three";

/**
 * A permanently-present light SLOT for one-off lighting events — the lightning strike, the fortress strike, a boss
 * slam (lighting review §4 hero moment 2). Not a `THREE.PointLight`: adding a light to the scene changes every lit
 * material's program (a hitch the moment the strike lands), and even a zero-intensity point light is a loop iteration
 * in every PBR fragment, every frame. This is a uniform block patched into the HQ materials instead: a point of
 * coloured light with a smooth range falloff, diffuse only, no shadow, and a uniform branch that skips all of it while
 * the slot is dark (intensity 0 — the normal state), so it is free between strikes.
 *
 * Phase 2 drives it (`StrikeLight.fire(pos, colour, intensity, range, seconds)`); Phase 1 only lays the pipe. The
 * ground shader can adopt it the same way it adopts cloud shadows (`inject` needs a world-position varying).
 */
export class StrikeLight {
  static readonly uniforms = {
    uStrikePos: { value: new THREE.Vector3() },
    /** Colour × intensity (linear). Black = off; the shader skips the slot entirely. */
    uStrikeCol: { value: new THREE.Color(0, 0, 0) },
    uStrikeRange: { value: 6 },
  };

  private static readonly base = new THREE.Color();
  private static peak = 0;
  private static t = 0;
  private static dur = 0;

  /**
   * Lights the slot at `pos` and fades it out over `seconds` (quadratic ease-out). A new call replaces the current one.
   * Defaults are the review's lightning strike: cool `0xcfe0ff`, intensity 40 at the core, range 6.
   */
  static fire(pos: THREE.Vector3, colour = 0xcfe0ff, intensity = 40, range = 6, seconds = 0.12): void {
    StrikeLight.uniforms.uStrikePos.value.copy(pos);
    StrikeLight.uniforms.uStrikeRange.value = range;
    StrikeLight.base.set(colour);
    StrikeLight.peak = intensity;
    StrikeLight.t = 0;
    StrikeLight.dur = Math.max(1e-3, seconds);
    StrikeLight.uniforms.uStrikeCol.value.copy(StrikeLight.base).multiplyScalar(intensity);
  }

  /** Advances the fade; SceneRig.update calls it every frame. */
  static update(dt: number): void {
    if (StrikeLight.peak <= 0) return;
    StrikeLight.t += dt;
    const k = Math.min(1, StrikeLight.t / StrikeLight.dur);
    const a = (1 - k) * (1 - k);
    StrikeLight.uniforms.uStrikeCol.value.copy(StrikeLight.base).multiplyScalar(StrikeLight.peak * a);
    if (k >= 1) { StrikeLight.peak = 0; StrikeLight.uniforms.uStrikeCol.value.setRGB(0, 0, 0); }
  }

  /**
   * Adds the slot to a built-in lit material in `onBeforeCompile`. `worldPos` names a vec3 varying holding the
   * fragment's world position that the shader already has (CloudShadows.inject provides `vCloudWP`). Diffuse only,
   * Lambert against the perturbed view-space normal; added after the direct lights so AO and cloud shade do not dim a
   * flash that is brighter than the sun.
   */
  static inject(shader: { uniforms: Record<string, THREE.IUniform>; fragmentShader: string }, worldPos: string): boolean {
    if (!shader.fragmentShader.includes("#include <lights_fragment_end>") || !shader.fragmentShader.includes(`varying vec3 ${worldPos};`)) return false;
    if (shader.fragmentShader.includes("uniform vec3 uStrikeCol;")) return true;
    Object.assign(shader.uniforms, StrikeLight.uniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\nuniform vec3 uStrikePos;\nuniform vec3 uStrikeCol;\nuniform float uStrikeRange;")
      .replace("#include <aomap_fragment>", `#include <aomap_fragment>
  if (uStrikeCol.r + uStrikeCol.g + uStrikeCol.b > 0.0) {
    vec3 skL = uStrikePos - ${worldPos};
    float skD = length(skL);
    float skFall = pow(saturate(1.0 - pow(skD / uStrikeRange, 4.0)), 2.0) / max(skD * skD, 0.25);
    vec3 skLv = normalize((viewMatrix * vec4(skL, 0.0)).xyz);
    reflectedLight.directDiffuse += BRDF_Lambert(diffuseColor.rgb) * uStrikeCol * skFall * saturate(dot(normal, skLv));
  }`);
    return true;
  }
}
